import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useStore } from '../store/useStore';
import { useNotifications } from '../hooks/useNotifications';
import { useIsMobile } from '../hooks/useIsMobile';
import { api, infoDelegacion, salirDelegado, cerrarSesionEnServidor } from '../lib/api';
import { Button } from './ui/button';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from './ui/tooltip';
import CommandPalette from './CommandPalette';
import LimiteDeError from './LimiteDeError';
import AyudaRapida from './AyudaRapida';
import { BrandLogo } from './BrandLogo';
import {
  Mail, Settings as SettingsIcon, Shield, Sun, Moon, Search, SlidersHorizontal,
  LogOut, Bell, MailOpen, Volume2, VolumeX, BellRing, X, Menu,
  ChevronDown, ShieldAlert, HardDrive, AtSign, MessageSquareWarning,
  CalendarDays, CalendarCheck, CalendarClock, Share2, BellDot, CircleHelp,
} from 'lucide-react';
import { fmtFull, initials } from '../lib/format';
import { pushSupported, subscribePush, isPushSubscribed, unsubscribePush } from '../lib/push';
import { toast } from 'sonner';
import { BUZON_CASOS } from '../lib/modulos';
import LlamadaEntrante from '../meet/LlamadaEntrante';
import BarraTransferencias from './drive/BarraTransferencias';
import { precalentarCuandoEsteLibre } from '../lib/precargaMotor';
import MailSidebar from './mail/MailSidebar';
import { Sheet, SheetContent, SheetTitle } from './ui/sheet';

const NAV_TODOS = [
  { to: '/mail', icon: Mail, label: 'Correo', short: 'Correo', testid: 'nav-mail' },
  { to: '/drive', icon: HardDrive, label: 'Drive', short: 'Drive', testid: 'nav-drive' },
  { to: '/calendar', icon: CalendarDays, label: 'Calendario', short: 'Agenda', testid: 'nav-calendar' },
  // Buzón de quejas y sugerencias (casos de Orvian Forms, ver pages/Casos.jsx)
  { to: '/casos', icon: MessageSquareWarning, label: 'Casos', short: 'Casos', testid: 'nav-casos' },
  { to: '/settings', icon: SettingsIcon, label: 'Ajustes', short: 'Ajustes', testid: 'nav-settings' },
];
const NAV = NAV_TODOS.filter((n) => BUZON_CASOS || n.to !== '/casos');

export default function AppShell() {
  const nav = useNavigate();
  const loc = useLocation();
  const isMobile = useIsMobile();
  const {
    user, setUser, logout, theme, toggleTheme, openComposer, setPaletteOpen,
    notifications, markNotificationsRead, soundEnabled, toggleSound, wsStatus,
    mobileFoldersOpen, setMobileFoldersOpen, toggleMobileFolders, setSelectedUid,
    listSearch, setListSearch, driveSearch, setDriveSearch, openMessage, setActiveFolder, setActiveAccount,
  } = useStore();
  useNotifications();
  const [showPushBanner, setShowPushBanner] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  /** Barra lateral contraída (68px). El estado se refleja como clase en <html>,
   *  igual que el tema — así MailSidebar (que cuelga de otra rama del árbol)
   *  se adapta sin acoplar componentes. */
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem('zuno_sidebar') === 'collapsed'; } catch (_) { return false; }
  });
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [ayudaAbierta, setAyudaAbierta] = useState(false);   // ayuda rápida (M31)
  const [notifOpen, setNotifOpen] = useState(false);
  const userMenuRef = useRef(null);
  const notifRef = useRef(null);

  /* Acceso delegado: este navegador está dentro del buzón de otra persona.
     Se lee de localStorage (no del store) porque debe sobrevivir a la recarga
     completa con la que se entra y se sale de la delegación. */
  const delegacion = infoDelegacion();
  const [saliendoDeleg, setSaliendoDeleg] = useState(false);

  const salirDelBuzon = async () => {
    setSaliendoDeleg(true);
    // Solo deja constancia de la salida; si falla, se sale igual.
    try { await api.post('/auth/impersonation/stop'); } catch (_) {}
    salirDelegado();
    window.location.href = '/admin';
  };

  /* Contraseña rechazada por el proveedor (A1). El servidor deja de sincronizar
     y de enviar con esa cuenta, y antes la bandeja seguía viéndose normal:
     un aviso que no se puede cerrar hasta volver a entrar con la contraseña. */
  const { data: cuentas = [] } = useQuery({
    queryKey: ['accounts'],
    queryFn: async () => {
      const res = await api.get('/accounts');
      if (Array.isArray(res.data)) return res.data;
      if (Array.isArray(res.data?.accounts)) return res.data.accounts;
      return [];
    },
    refetchInterval: 2 * 60_000,
  });
  const cuentaRechazada = cuentas.find((c) => c.acceso_rechazado);

  /* Sin red (M8): antes solo se notaba si la lista estaba vacía; con la lista cargada se
     trabajaba sobre datos viejos sin saberlo. */
  const [sinRed, setSinRed] = useState(() => typeof navigator !== 'undefined' && navigator.onLine === false);
  useEffect(() => {
    const cambia = () => setSinRed(navigator.onLine === false);
    window.addEventListener('online', cambia);
    window.addEventListener('offline', cambia);
    return () => { window.removeEventListener('online', cambia); window.removeEventListener('offline', cambia); };
  }, []);

  const volverAEntrar = async () => {
    // Misma persona, mismo navegador: no se da de baja el push (volverá a ser suyo al entrar).
    const correo = cuentaRechazada?.email || user?.email || '';
    await cerrarSesionEnServidor();   // S3: la sesión vieja no queda viva en el servidor
    logout();
    nav(`/login?motivo=credenciales&correo=${encodeURIComponent(correo)}`);
  };

  const onMail = loc.pathname.startsWith('/mail');
  /* En Drive el buscador de la barra superior busca en Drive: tener el del
     correo arriba y el de Drive debajo eran dos cajas iguales, y escribir en la
     de arriba te sacaba de Drive. */
  const onDrive = loc.pathname.startsWith('/drive');
  /* En el Calendario no hay barra lateral del correo (la izquierda es el minicalendario) y el ☰
     no hacía nada: ahora abre la navegación completa como panel encima. Se cierra con Esc,
     clic fuera o al elegir una opción (cambia la ruta o la carpeta). */
  const enCalendario = loc.pathname.startsWith('/calendar');
  const [navAbierta, setNavAbierta] = useState(false);
  useEffect(() => { setNavAbierta(false); }, [loc.pathname]);
  const busqueda = onDrive ? driveSearch : listSearch;
  const setBusqueda = onDrive ? setDriveSearch : setListSearch;
  const [pushOn, setPushOn] = useState(false);

  useEffect(() => {
    if (!pushSupported()) return;
    isPushSubscribed().then(setPushOn).catch(() => {});
  }, []);

  // O4 (auditoría 2026-10-09): el editor de Word/Excel se baja en segundo plano mientras se usa el correo
  useEffect(() => precalentarCuandoEsteLibre(), []);

  /* El usuario guardado en el navegador puede ser de hace semanas. Se refresca
     una vez al abrir para que el rol y lo que ya ha visto (introducciones)
     vengan del servidor y no de una copia vieja de este equipo. */
  useEffect(() => {
    let vivo = true;
    api.get('/auth/me')
      .then((r) => { if (vivo && r.data?.id) setUser({ ...r.data }); })
      .catch(() => {});
    return () => { vivo = false; };
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  /** Abre un correo concreto (desde una notificación in-app o del service worker). */
  const goToMail = useCallback((accountId, folder, uid) => {
    if (!accountId || !uid) { nav('/mail'); return; }
    setActiveAccount(accountId);
    setActiveFolder(folder || 'inbox');
    openMessage(accountId, folder || 'inbox', String(uid));
    nav('/mail');
  }, [nav, openMessage, setActiveFolder, setActiveAccount]);

  /** El service worker avisa cuando se pulsa una notificación con la pestaña ya
   *  abierta: navegamos al correo sin recargar la aplicación. */
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return undefined;
    const onMsg = (ev) => {
      const d = ev.data || {};
      if (d.type !== 'open-mail') return;
      goToMail(d.account_id, d.folder, d.uid);
      // Acuse de recibo: sin él el service worker no sabe si alguien atendió y
      // tendría que elegir a ciegas entre navegar (recargando) o no hacer nada.
      try { if (ev.ports && ev.ports[0]) ev.ports[0].postMessage({ ok: true }); } catch (_) {}
    };
    navigator.serviceWorker.addEventListener('message', onMsg);
    return () => navigator.serviceWorker.removeEventListener('message', onMsg);
  }, [goToMail]);

  // Refleja la contracción en <html> (nunca en móvil: allí la barra vive en un drawer)
  useEffect(() => {
    const root = document.documentElement;
    if (!isMobile && sidebarCollapsed) root.classList.add('sidebar-collapsed');
    else root.classList.remove('sidebar-collapsed');
    return () => root.classList.remove('sidebar-collapsed');
  }, [isMobile, sidebarCollapsed]);

  // Cierre de menús flotantes: clic fuera + Escape
  useEffect(() => {
    if (!userMenuOpen && !notifOpen) return undefined;
    const onDown = (e) => {
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
      if (notifOpen && notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { setUserMenuOpen(false); setNotifOpen(false); }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [userMenuOpen, notifOpen]);

  useEffect(() => {
    if (!pushSupported()) return undefined;
    // En una sesión delegada jamás: `POST /push/subscribe` se queda con el
    // endpoint del navegador para el usuario que lo pide, así que el admin
    // perdería sus propios avisos y empezaría a recibir los del buzón visitado.
    if (infoDelegacion()) return undefined;
    try {
      if (localStorage.getItem('zuno_push_banner') === 'dismissed') return undefined;
      if (localStorage.getItem('zuno_push') === 'on' && Notification.permission === 'granted') return undefined;
    } catch (_) {}
    if (Notification.permission === 'denied' || Notification.permission === 'granted') return undefined;
    const t = setTimeout(() => setShowPushBanner(true), 2500);
    return () => clearTimeout(t);
  }, []);

  const enablePush = async () => {
    setPushBusy(true);
    try {
      await subscribePush();
      setShowPushBanner(false);
      try { localStorage.setItem('zuno_push_banner', 'dismissed'); } catch (_) {}
      toast.success('Notificaciones activadas');
    } catch (err) {
      toast.error(err.message || 'No se pudieron activar las notificaciones');
    } finally {
      setPushBusy(false);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
      const escribiendo = /input|textarea|select/i.test(document.activeElement?.tagName) || document.activeElement?.isContentEditable;
      if (e.key === '?' && !e.ctrlKey && !e.metaKey && !e.altKey && !escribiendo) { e.preventDefault(); setAyudaAbierta(true); return; }
      // Sin modificadores: Ctrl+C para copiar texto del lector abría el redactor (M15).
      // En el Calendario «c» es «crear evento» (lo atiende CalendarPage), no «redactar» (K12)
      if (!isMobile && e.key === 'c' && !window.location.pathname.startsWith('/calendar') && !e.ctrlKey && !e.metaKey && !e.altKey && !/input|textarea|select/i.test(document.activeElement?.tagName) && !document.activeElement?.isContentEditable && !document.querySelector('[role="dialog"][data-state="open"]')) {
        openComposer('new');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [setPaletteOpen, openComposer, isMobile]);

  const isAdmin = user?.role === 'owner' || user?.role === 'admin';
  const items = [...NAV];
  if (isAdmin) {
    items.push({ to: '/admin', icon: Shield, label: 'Admin', short: 'Admin', testid: 'nav-admin' });
  }
  const unreadNotifs = notifications.filter((n) => !n.read).length;
  const live = wsStatus === 'connected';

  /** Hamburguesa única: en móvil abre carpetas / vuelve atrás; en escritorio contrae la barra */
  const onMenuClick = () => {
    if (enCalendario) { setNavAbierta((v) => !v); return; }
    if (isMobile) {
      // ☰ es siempre el menú (M20): volver del correo es la ← del propio lector. Antes este botón
      // se volvía una X «Volver a la bandeja» y había dos controles para lo mismo.
      if (onMail) { toggleMobileFolders(); return; }
      nav('/mail');
      setTimeout(() => setMobileFoldersOpen(true), 50);
      return;
    }
    setSidebarCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem('zuno_sidebar', next ? 'collapsed' : 'expanded'); } catch (_) {}
      return next;
    });
  };

  const goSettings = (hash) => {
    setUserMenuOpen(false);
    nav(hash ? `/settings${hash}` : '/settings');
  };

  const markAllRead = async () => {
    markNotificationsRead();
    try { await api.post('/notifications/read'); } catch (_) {}
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="h-screen w-full flex flex-col overflow-hidden" style={{ background: 'var(--bg)' }}>
        {/* ---- Estás dentro del buzón de otra persona ----
            Franja permanente y de color distinto: es lo único que separa
            "leo mi correo" de "leo el de alguien más". No se puede cerrar. */}
        {delegacion && (
          <div
            data-testid="delegacion-banner"
            className="shrink-0 px-3 sm:px-4 py-2 flex items-center gap-2 text-xs sm:text-sm"
            style={{ background: '#7c2d12', color: '#fff' }}
          >
            <ShieldAlert className="h-4 w-4 shrink-0" />
            <div className="flex-1 min-w-0 truncate">
              <span className="font-medium">Estás en el buzón de {delegacion.buzon?.email}</span>
              <span className="hidden sm:inline" style={{ opacity: 0.85 }}>
                {' '}— acceso delegado como {delegacion.quien?.email}. Todo queda registrado.
              </span>
            </div>
            <Button
              size="sm"
              variant="secondary"
              onClick={salirDelBuzon}
              disabled={saliendoDeleg}
              className="h-8 shrink-0 text-xs rounded-[10px]"
              data-testid="delegacion-salir"
            >
              {saliendoDeleg ? '…' : 'Salir del buzón'}
            </Button>
          </div>
        )}

        {sinRed && (
          <div
            role="status"
            data-testid="sin-conexion-banner"
            className="shrink-0 px-3 sm:px-4 py-1.5 flex items-center gap-2 text-xs sm:text-sm"
            style={{ background: 'var(--warn-soft)', color: 'var(--text)' }}
          >
            <span className="font-medium">Sin conexión a internet.</span>
            <span style={{ color: 'var(--text-2)' }}>Ves lo último que se cargó; enviar y mover correos no funcionará hasta que vuelva.</span>
          </div>
        )}

        {cuentaRechazada && (
          <div
            role="alert"
            data-testid="acceso-rechazado-banner"
            className="shrink-0 px-3 sm:px-4 py-2 flex items-center gap-2 text-xs sm:text-sm"
            style={{ background: '#991b1b', color: '#fff' }}
          >
            <ShieldAlert className="h-4 w-4 shrink-0" />
            <div className="flex-1 min-w-0">
              <span className="font-medium">
                {delegacion
                  ? `El proveedor rechaza la contraseña de ${cuentaRechazada.email}.`
                  : `Tu proveedor rechazó la contraseña de ${cuentaRechazada.email}.`}
              </span>
              <span style={{ opacity: 0.9 }}>
                {' '}El envío y la llegada de correo nuevo están en pausa
                {cuentaRechazada.acceso_rechazado_at ? ` desde el ${fmtFull(cuentaRechazada.acceso_rechazado_at)}` : ''}.
                {delegacion
                  ? ' Esa persona tiene que volver a entrar con su contraseña actual.'
                  : ' Vuelve a entrar con la contraseña actual de tu buzón (no con passkey).'}
              </span>
            </div>
            {!delegacion && (
              <Button
                size="sm"
                variant="secondary"
                onClick={volverAEntrar}
                className="h-8 shrink-0 text-xs rounded-[10px]"
                data-testid="acceso-rechazado-reentrar"
              >
                Volver a entrar
              </Button>
            )}
          </div>
        )}

        {showPushBanner && (
          <div
            data-testid="push-enable-banner"
            className="shrink-0 px-3 sm:px-4 py-2 flex items-center gap-2 text-xs sm:text-sm"
            style={{ background: 'var(--accent-soft)', color: 'var(--text)' }}
          >
            <BellRing className="h-4 w-4 shrink-0" style={{ color: 'var(--accent)' }} />
            <div className="flex-1 min-w-0 truncate">
              <span className="font-medium">Activa notificaciones</span>
              <span className="hidden sm:inline" style={{ color: 'var(--text-2)' }}> — aunque cierres la pestaña</span>
            </div>
            <Button size="sm" onClick={enablePush} disabled={pushBusy} data-testid="push-enable-btn" className="h-8 shrink-0 text-xs rounded-[10px]">
              {pushBusy ? '…' : 'Activar'}
            </Button>
            <button
              type="button"
              onClick={() => { setShowPushBanner(false); try { localStorage.setItem('zuno_push_banner', 'dismissed'); } catch (_) {} }}
              className="od-icon-btn-sm shrink-0"
              aria-label="Cerrar"
              data-testid="push-banner-dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* ===================== Barra superior ===================== */}
        <header className={`gmail-topbar relative ${isMobile ? 'gmail-topbar--mobile' : ''}`}>
          <button
            type="button"
            className="gmail-icon-btn"
            aria-label={isMobile || enCalendario ? 'Menú' : 'Contraer barra lateral'}
            aria-expanded={enCalendario ? navAbierta : isMobile ? mobileFoldersOpen : !sidebarCollapsed}
            onClick={onMenuClick}
            data-testid="topbar-menu"
          >
            <Menu className="h-5 w-5" />
          </button>

          {/* Marca — en escritorio el wordmark real (ya lleva la marca);
              en móvil el cuadro compacto + nombre, que se lee mejor en pequeño. */}
          <button
            type="button"
            onClick={() => { setSelectedUid(null); nav('/mail'); }}
            className="flex items-center gap-[9px] border-0 bg-transparent cursor-pointer p-0 shrink-0"
            aria-label="Ir a la bandeja"
          >
            {isMobile
              ? <span className="od-brand-mark" aria-hidden="true">O</span>
              : <BrandLogo showText className="!min-w-0 !p-0" />}
          </button>
          {isMobile && (
            <span className="gmail-topbar-title truncate font-semibold text-[16px]" style={{ color: 'var(--text)' }}>
              Orvian
            </span>
          )}

          {/* Buscador central (escritorio) / icono (móvil). En Drive móvil no hay
              icono: la propia página trae su caja de búsqueda. */}
          {isMobile && onDrive ? (
            <span className="ml-auto" />
          ) : isMobile ? (
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              data-testid="topbar-search"
              className="gmail-icon-btn ml-auto"
              aria-label="Buscar"
            >
              <Search className="h-5 w-5" />
            </button>
          ) : (
            <div className="gmail-search" data-testid="topbar-search">
              <Search className="h-[18px] w-[18px] shrink-0" style={{ color: 'var(--text-3)' }} />
              <input
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') setBusqueda(''); }}
                onFocus={() => { if (!onMail && !onDrive) nav('/mail'); }}
                placeholder={onDrive ? 'Buscar en Drive' : 'Buscar en el correo'}
                className="flex-1 min-w-0 text-[14px]"
                data-testid="topbar-search-input"
                aria-label={onDrive ? 'Buscar en Drive' : 'Buscar en el correo'}
              />
              {busqueda ? (
                <button type="button" className="od-icon-btn-sm" onClick={() => setBusqueda('')} aria-label="Limpiar búsqueda">
                  <X className="h-4 w-4" />
                </button>
              ) : null}
              {/* La búsqueda avanzada es del correo: en Drive no filtra nada. */}
              {!onDrive && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button type="button" className="od-icon-btn-sm" onClick={() => setPaletteOpen(true)} aria-label="Búsqueda avanzada">
                      <SlidersHorizontal className="h-4 w-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>Búsqueda avanzada · Ctrl+K</TooltipContent>
                </Tooltip>
              )}
            </div>
          )}

          {/* Acciones */}
          <div className={`flex items-center gap-1 ${isMobile ? '' : 'ml-auto'}`}>
            {!isMobile && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="gmail-icon-btn"
                    onClick={() => nav(loc.pathname.startsWith('/drive') ? '/mail' : '/drive')}
                    data-testid="nav-drive"
                    aria-label={loc.pathname.startsWith('/drive') ? 'Correo' : 'Drive'}
                  >
                    {loc.pathname.startsWith('/drive') ? <Mail className="h-5 w-5" /> : <HardDrive className="h-5 w-5" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{loc.pathname.startsWith('/drive') ? 'Volver al correo' : 'Drive'}</TooltipContent>
              </Tooltip>
            )}
            {BUZON_CASOS && !isMobile && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="gmail-icon-btn"
                    onClick={() => nav(loc.pathname.startsWith('/casos') ? '/mail' : '/casos')}
                    data-testid="nav-casos"
                    aria-label={loc.pathname.startsWith('/casos') ? 'Correo' : 'Casos'}
                  >
                    {loc.pathname.startsWith('/casos') ? <Mail className="h-5 w-5" /> : <MessageSquareWarning className="h-5 w-5" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{loc.pathname.startsWith('/casos') ? 'Volver al correo' : 'Casos (quejas y sugerencias)'}</TooltipContent>
              </Tooltip>
            )}
            {!isMobile && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="gmail-icon-btn" onClick={toggleSound} aria-label="Sonido">
                    {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{soundEnabled ? 'Silenciar avisos' : 'Activar sonido'}</TooltipContent>
              </Tooltip>
            )}

            {/* Notificaciones */}
            <div className="relative" ref={notifRef}>
              <button
                type="button"
                className="gmail-icon-btn"
                onClick={() => { setNotifOpen((v) => !v); setUserMenuOpen(false); }}
                data-testid="notifications-bell"
                aria-label="Notificaciones"
                aria-expanded={notifOpen}
              >
                <span className="relative flex">
                  <Bell className="h-5 w-5" />
                  {unreadNotifs > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 rounded-full"
                      style={{ width: 7, height: 7, background: 'var(--accent)', border: '1.5px solid var(--panel)' }}
                    />
                  )}
                </span>
              </button>

              {notifOpen && (
                <div
                  className="od-panel-pop absolute z-[65] flex flex-col overflow-hidden"
                  style={{ top: 46, right: 0, width: 360, maxHeight: '70vh' }}
                  data-testid="notifications-panel"
                >
                  <div className="flex items-center gap-2 px-4 pt-3.5 pb-2.5">
                    <span className="flex-1 text-[15px] font-semibold" style={{ color: 'var(--text)' }}>Notificaciones</span>
                    <span
                      className="text-[11px] font-medium inline-flex items-center gap-1.5"
                      style={{ color: live ? 'var(--good)' : 'var(--text-3)' }}
                      title={live ? 'Conectado en tiempo real' : 'Sin conexión en tiempo real'}
                    >
                      <span className="rounded-full" style={{ width: 6, height: 6, background: live ? 'var(--good)' : 'var(--text-3)' }} />
                      {live ? 'En vivo' : 'Sin conexión'}
                    </span>
                    {notifications.length > 0 && (
                      <button
                        type="button"
                        onClick={markAllRead}
                        className="text-[12px] font-semibold bg-transparent border-0 cursor-pointer"
                        style={{ color: 'var(--accent)' }}
                      >
                        Marcar leídas
                      </button>
                    )}
                  </div>
                  {pushSupported() && !pushOn && (
                    <div
                      className="mx-2 mb-2 p-3 flex items-start gap-2.5"
                      style={{ borderRadius: 11, background: 'var(--accent-soft)' }}
                      data-testid="push-enable-card"
                    >
                      <BellRing className="h-[17px] w-[17px] shrink-0 mt-0.5" style={{ color: 'var(--accent)' }} />
                      <div className="min-w-0 flex-1">
                        <div className="text-[13px] font-semibold" style={{ color: 'var(--text)' }}>
                          Avisos con la app cerrada
                        </div>
                        <div className="text-[12px] leading-snug mt-0.5" style={{ color: 'var(--text-2)' }}>
                          Recibe el correo nuevo aunque no tengas Orvian abierto.
                        </div>
                        <button
                          type="button"
                          className="od-btn-primary mt-2.5"
                          style={{ padding: '6px 12px', fontSize: 12 }}
                          disabled={pushBusy}
                          onClick={async () => {
                            setPushBusy(true);
                            try {
                              await subscribePush();
                              setPushOn(true);
                              try { localStorage.setItem('zuno_push', 'on'); } catch (_) {}
                              toast.success('Notificaciones activadas');
                            } catch (err) {
                              toast.error(err?.message || 'No se pudieron activar');
                            } finally { setPushBusy(false); }
                          }}
                        >
                          {pushBusy ? 'Activando…' : 'Activar'}
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="od-scroll flex flex-col gap-1.5 px-2 pb-2.5">
                    {notifications.length === 0 ? (
                      <div className="px-3 py-10 text-center text-sm" style={{ color: 'var(--text-3)' }}>
                        Sin notificaciones
                      </div>
                    ) : notifications.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        data-testid="notification-item"
                        onClick={() => {
                          setNotifOpen(false);
                          // Menciones de Drive: llevan su enlace al archivo, no un correo
                          if (n.link && !n.account_id) nav(n.link);
                          else goToMail(n.account_id, n.folder || 'inbox', n.uid);
                        }}
                        className="flex gap-[11px] p-[11px] text-left border-0 cursor-pointer transition-colors"
                        style={{
                          borderRadius: 11,
                          background: 'var(--bg)',
                          borderLeft: `3px solid ${n.read ? 'var(--line-2)' : 'var(--accent)'}`,
                        }}
                      >
                        <span
                          className="grid place-items-center shrink-0"
                          style={{ width: 34, height: 34, borderRadius: 9, background: 'var(--accent-soft)', color: 'var(--accent)' }}
                        >
                          {n.type === 'drive_mention' ? <AtSign className="h-[17px] w-[17px]" />
                            : n.type === 'buzon_caso' ? <MessageSquareWarning className="h-[17px] w-[17px]" />
                              : n.type === 'cal_cita' ? <CalendarCheck className="h-[17px] w-[17px]" />
                                : n.type === 'cal_recordatorio' ? <BellDot className="h-[17px] w-[17px]" />
                                  : n.type === 'cal_compartido' ? <Share2 className="h-[17px] w-[17px]" />
                                    : n.type === 'cal_respuesta' ? <CalendarClock className="h-[17px] w-[17px]" />
                                      : n.type === 'cal_invitacion' ? <CalendarDays className="h-[17px] w-[17px]" />
                                        : <MailOpen className="h-[17px] w-[17px]" />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="flex gap-2 items-baseline">
                            <span className="flex-1 min-w-0 truncate text-[13px] font-semibold" style={{ color: 'var(--text)' }}>{n.title}</span>
                            {n.accountEmail && (
                              <span className="text-[11px] shrink-0 truncate max-w-[120px]" style={{ color: 'var(--text-3)' }}>{n.accountEmail}</span>
                            )}
                          </span>
                          <span className="block text-[12px] leading-[1.4] truncate" style={{ color: 'var(--text-2)' }}>{n.subject}</span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {!isMobile && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" className="gmail-icon-btn" onClick={toggleTheme} data-testid="settings-theme-toggle" aria-label="Tema">
                    {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                  </button>
                </TooltipTrigger>
                <TooltipContent>Tema {theme === 'dark' ? 'claro' : 'oscuro'}</TooltipContent>
              </Tooltip>
            )}

            {!isMobile && <div style={{ width: 1, height: 24, background: 'var(--line)', margin: '0 6px' }} />}

            {/* Menú de usuario */}
            <div className="relative" ref={userMenuRef}>
              <button
                type="button"
                data-testid="user-menu"
                aria-label="Cuenta"
                aria-expanded={userMenuOpen}
                onClick={() => { setUserMenuOpen((v) => !v); setNotifOpen(false); }}
                className="flex items-center gap-2 bg-transparent border-0 cursor-pointer rounded-[10px] transition-colors"
                style={{ padding: '4px 6px 4px 4px' }}
              >
                <span className="gmail-avatar">{initials(user?.name, user?.email)}</span>
                {!isMobile && <ChevronDown className="h-4 w-4" style={{ color: 'var(--text-3)' }} />}
              </button>

              {userMenuOpen && (
                <div
                  className="od-panel-pop absolute z-[60] p-1.5"
                  style={{ top: 46, right: 0, width: 260 }}
                  data-testid="user-menu-panel"
                >
                  <div className="flex gap-[11px] px-3 pt-3 pb-2.5">
                    <span className="gmail-avatar shrink-0" style={{ width: 42, height: 42, fontSize: 16 }}>
                      {initials(user?.name, user?.email)}
                    </span>
                    <div className="min-w-0">
                      <div className="text-[14px] font-semibold truncate" style={{ color: 'var(--text)' }}>{user?.name}</div>
                      <div className="text-[12px] truncate" style={{ color: 'var(--text-3)' }}>{user?.email}</div>
                    </div>
                  </div>
                  <div style={{ height: 1, background: 'var(--line)', margin: '2px 0 6px' }} />
                  {/* Un solo nombre para Ajustes (M19): antes «Mi cuenta» y «Configuración» llevaban al mismo sitio */}
                  <button type="button" className="od-menu-row" onClick={() => goSettings()}>
                    <SettingsIcon className="h-[18px] w-[18px]" style={{ color: 'var(--text-3)' }} />Ajustes
                  </button>
                  <button type="button" className="od-menu-row" onClick={() => goSettings('#seguridad')}>
                    <Shield className="h-[18px] w-[18px]" style={{ color: 'var(--text-3)' }} />Seguridad
                  </button>
                  <button type="button" className="od-menu-row" onClick={() => { setUserMenuOpen(false); setAyudaAbierta(true); }} data-testid="menu-ayuda">
                    <CircleHelp className="h-[18px] w-[18px]" style={{ color: 'var(--text-3)' }} />Ayuda y atajos
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      className="od-menu-row"
                      data-testid="nav-admin"
                      onClick={() => { setUserMenuOpen(false); nav('/admin'); }}
                    >
                      <Shield className="h-[18px] w-[18px]" style={{ color: 'var(--accent)' }} />Administración
                    </button>
                  )}
                  {isMobile && (
                    <button type="button" className="od-menu-row" onClick={() => { toggleTheme(); setUserMenuOpen(false); }}>
                      {theme === 'dark'
                        ? <Sun className="h-[18px] w-[18px]" style={{ color: 'var(--text-3)' }} />
                        : <Moon className="h-[18px] w-[18px]" style={{ color: 'var(--text-3)' }} />}
                      Tema {theme === 'dark' ? 'claro' : 'oscuro'}
                    </button>
                  )}
                  <div style={{ height: 1, background: 'var(--line)', margin: '6px 0' }} />
                  <button
                    type="button"
                    className="od-menu-row"
                    data-testid="logout-button"
                    onClick={async () => {
                      setUserMenuOpen(false);
                      // Dentro de un buzón ajeno, "cerrar sesión" significa SALIR
                      // de la delegación: la sesión propia del admin sigue viva y
                      // el push de este navegador es suyo, no del buzón visitado.
                      if (delegacion) { await salirDelBuzon(); return; }
                      // Antes de soltar el token: dar de baja el push. Si no, este
                      // navegador seguiría enseñando remitente y asunto de un buzón
                      // cuya sesión acaba de cerrarse (equipos compartidos).
                      try { await unsubscribePush(); } catch (_) {}
                      await cerrarSesionEnServidor();   // S3: sin esto la sesión seguía valiendo en el servidor
                      logout();
                      nav('/login');
                    }}
                    style={{ color: 'var(--danger)' }}
                  >
                    <LogOut className="h-[18px] w-[18px]" style={{ color: 'var(--danger)' }} />
                    {delegacion ? 'Salir del buzón' : 'Cerrar sesión'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <div className="flex-1 flex min-h-0">
          <main className={`flex-1 min-w-0 min-h-0 ${isMobile ? 'pb-[calc(3.5rem+env(safe-area-inset-bottom,0px))]' : ''}`}>
            {/* key = sección: cambiar de Correo a Drive (p. ej.) limpia el error y la barra de arriba sigue viva */}
            <LimiteDeError key={loc.pathname}>
              <Outlet />
            </LimiteDeError>
            <AyudaRapida open={ayudaAbierta} onOpenChange={setAyudaAbierta} />
            {enCalendario && (
              <Sheet open={navAbierta} onOpenChange={setNavAbierta}>
                <SheetContent
                  side="left"
                  sinCerrar
                  overlayClassName="bg-black/30"
                  className="p-0 w-[244px] max-w-[86vw] border-0 nav-drawer outline-none"
                  aria-describedby={undefined}
                  data-testid="nav-drawer"
                >
                  <SheetTitle className="sr-only">Navegación</SheetTitle>
                  <MailSidebar accounts={cuentas} onNavigate={() => setNavAbierta(false)} />
                </SheetContent>
              </Sheet>
            )}
          </main>
        </div>

        {isMobile && (
          <nav className="gmail-bottom-nav" data-testid="mobile-bottom-nav">
            {items.map((it) => {
              const active = loc.pathname === it.to || (it.to !== '/mail' && loc.pathname.startsWith(it.to));
              const Icon = it.icon;
              return (
                <button
                  key={it.to}
                  type="button"
                  onClick={() => {
                    setMobileFoldersOpen(false);
                    if (it.to === '/mail') setSelectedUid(null);
                    nav(it.to);
                  }}
                  data-testid={`${it.testid}-mobile`}
                  className={`gmail-bottom-nav-item ${active ? 'gmail-bottom-nav-item--active' : ''}`}
                >
                  <Icon className="h-5 w-5" strokeWidth={active ? 2.25 : 1.75} />
                  <span>{it.short || it.label}</span>
                </button>
              );
            })}
          </nav>
        )}
        <CommandPalette />
      </div>
      {/* Llamadas directas de Orvian Meet: suena aquí también (solo si están encendidas en el servidor) */}
      <LlamadaEntrante enPortal />
      {/* Subidas de Drive en curso (como MEGA): sigue a la vista aunque se cambie de sección */}
      <BarraTransferencias />
    </TooltipProvider>
  );
}
